import React, { useEffect, useState } from "react";
import { Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

import { createContractPatient, listFacilityLeads } from "@/lib/api";

export default function AddContractPatientDialog({ open, onClose, onCreated, defaultFacilityId }) {
  const [facilities, setFacilities] = useState([]);
  const [loadingFac, setLoadingFac] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    full_name: "", facility_id: defaultFacilityId || "", contract_number: "",
    phone: "", email: "", dob: "", mrn: "", gender: "",
    primary_language: "English", allergies: "", notes: "",
    authorized_services: "",
  });

  useEffect(() => {
    if (!open) return;
    setError("");
    setForm({
      full_name: "", facility_id: defaultFacilityId || "", contract_number: "",
      phone: "", email: "", dob: "", mrn: "", gender: "",
      primary_language: "English", allergies: "", notes: "",
      authorized_services: "",
    });
    setLoadingFac(true);
    listFacilityLeads()
      .then((rows) => setFacilities((rows || []).filter((f) => !f.is_archived && !f.is_binned)))
      .catch(() => toast.error("Failed to load facilities."))
      .finally(() => setLoadingFac(false));
  }, [open, defaultFacilityId]);

  const submit = async () => {
    if (!form.full_name.trim()) { setError("Full name is required."); return; }
    if (!form.facility_id) { setError("Assign a facility first."); return; }
    setSaving(true); setError("");
    try {
      const body = {
        full_name: form.full_name.trim(),
        facility_id: form.facility_id,
        contract_number: form.contract_number || null,
        phone: form.phone || null, email: form.email || null,
        dob: form.dob || null, mrn: form.mrn || null,
        gender: form.gender || null,
        primary_language: form.primary_language || "English",
        allergies: form.allergies || null, notes: form.notes || null,
        authorized_services: form.authorized_services
          ? form.authorized_services.split(",").map((s) => s.trim()).filter(Boolean) : [],
      };
      const created = await createContractPatient(body);
      toast.success("Contract patient added.");
      onCreated?.(created);
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Save failed.";
      setError(typeof msg === "string" ? msg : "Save failed.");
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col p-0"
                     data-testid="add-contract-patient-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b" style={{ borderColor: "#F1F5F9" }}>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-4 w-4" style={{ color: "#0F6D5D" }} />
            Add Contract Patient
          </DialogTitle>
          <DialogDescription>
            Patients tied to an active facility contract. No customer intake or Stripe checkout needed.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {error ? (
            <div className="rounded-lg border p-3 text-xs"
                 style={{ borderColor: "rgba(220,38,38,0.3)", background: "rgba(220,38,38,0.06)", color: "#991B1B" }}
                 data-testid="add-patient-error">
              {error}
            </div>
          ) : null}

          <div>
            <Label className="text-xs">Full name *</Label>
            <Input className="mt-1" value={form.full_name}
                   onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                   placeholder="Tyler Smith" data-testid="add-full_name" />
          </div>

          <div>
            <Label className="text-xs">Facility *</Label>
            <Select value={form.facility_id}
                    onValueChange={(v) => setForm((f) => ({ ...f, facility_id: v }))}>
              <SelectTrigger className="mt-1" data-testid="add-facility_id">
                <SelectValue placeholder={loadingFac ? "Loading…" : "Select a facility"} />
              </SelectTrigger>
              <SelectContent>
                {facilities.map((f) => (
                  <SelectItem key={f.id} value={f.id}>{f.facility_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Contract # (optional)</Label>
              <Input className="mt-1" value={form.contract_number} placeholder="F-2026-004"
                     onChange={(e) => setForm((f) => ({ ...f, contract_number: e.target.value }))}
                     data-testid="add-contract_number" />
            </div>
            <div>
              <Label className="text-xs">MRN (optional)</Label>
              <Input className="mt-1" value={form.mrn}
                     onChange={(e) => setForm((f) => ({ ...f, mrn: e.target.value }))}
                     data-testid="add-mrn" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Phone</Label>
              <Input className="mt-1" value={form.phone}
                     onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                     data-testid="add-phone" />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" className="mt-1" value={form.email}
                     onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                     data-testid="add-email" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Date of Birth</Label>
              <Input type="date" className="mt-1" value={form.dob}
                     onChange={(e) => setForm((f) => ({ ...f, dob: e.target.value }))}
                     data-testid="add-dob" />
            </div>
            <div>
              <Label className="text-xs">Gender</Label>
              <Select value={form.gender || ""} onValueChange={(v) => setForm((f) => ({ ...f, gender: v }))}>
                <SelectTrigger className="mt-1" data-testid="add-gender">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                  <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs">Allergies</Label>
            <Input className="mt-1" value={form.allergies} placeholder="None"
                   onChange={(e) => setForm((f) => ({ ...f, allergies: e.target.value }))}
                   data-testid="add-allergies" />
          </div>

          <div>
            <Label className="text-xs">Authorized services (comma-separated)</Label>
            <Input className="mt-1" value={form.authorized_services}
                   placeholder="Phlebotomy, Wellness Panels, Wound Care"
                   onChange={(e) => setForm((f) => ({ ...f, authorized_services: e.target.value }))}
                   data-testid="add-authorized_services" />
          </div>

          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea rows={3} className="mt-1" value={form.notes}
                      onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                      placeholder="Fall risk. Uses walker. Bring extra tubes."
                      data-testid="add-notes" />
          </div>
        </div>

        <DialogFooter className="px-6 py-3 border-t" style={{ borderColor: "#F1F5F9" }}>
          <Button variant="outline" onClick={onClose} className="h-9">Cancel</Button>
          <Button onClick={submit} disabled={saving || !form.full_name || !form.facility_id}
                  className="h-9" style={{ background: "#0B2545", color: "#FFF" }}
                  data-testid="add-patient-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <UserPlus className="h-4 w-4 mr-1.5" />}
            Add Patient
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
